/**
 * frontend/src/pages/valuation/_TuningSignoffColumn.tsx
 *
 * VAL-TUNING-SPLIT-1 — the sticky right-hand preview/acknowledgement/
 * continue column of ValuationTuning.tsx, split out under
 * docs/FILE_SIZE_POLICY.md. Presentational only — the Sign-off button's
 * click handler (navigate) stays on the screen, which
 * owns `id`/`body`/`navigate`, and is passed in as `onContinue`.
 */
import type { BasisBlock, ComputedPayload } from '@/types/valuationB3'
import { Button } from '@/components/ui/button'
import { TuningPreview } from './_TuningPreview'

interface TuningSignoffColumnProps {
  computed: ComputedPayload | null
  basis: BasisBlock | null
  pending: boolean
  runError: string | null
  blockers: string[]
  warnings: string[]
  isMarketOnly: boolean
  acknowledgeMarketOnly: boolean
  onAcknowledgeMarketOnly: (value: boolean) => void
  onContinue: () => void
  valueKind?: 'concluded' | 'calculated' | null
}

export function TuningSignoffColumn({
  computed,
  basis,
  pending,
  runError,
  blockers,
  warnings,
  isMarketOnly,
  acknowledgeMarketOnly,
  onAcknowledgeMarketOnly,
  onContinue,
  valueKind,
}: TuningSignoffColumnProps) {
  return (
    <div className="min-w-0 space-y-4 lg:sticky lg:top-4 lg:self-start">
      {/* VAL-UI-FIX-1 R2 — sticky keeps the preview on screen (R2a); this
         wrapper caps it to the viewport and lets it scroll internally so
         a tall preview never pushes its own top off-screen (R2b). Only
         the preview is capped — the acknowledgement checkbox and the
         sign-off button stay outside it, as plain siblings in the sticky
         column, so they are always visible without having to scroll the
         preview to find them. lg:-only (R2c); left column untouched (R2d). */}
      <div className="lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
        <TuningPreview
          computed={computed}
          basis={basis}
          stale={pending}
          error={runError}
          blockers={blockers}
          warnings={warnings}
          valueKind={valueKind}
        />
      </div>
      {isMarketOnly && (
        <label className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={acknowledgeMarketOnly}
            onChange={e => onAcknowledgeMarketOnly(e.target.checked)}
          />
          <span>
            No income approach value was supplied. This calculated value rests on the market
            approach alone and is <strong>not triangulated</strong> — I acknowledge this before
            continuing.
          </span>
        </label>
      )}
      <Button
        className="w-full"
        disabled={!computed || blockers.length > 0 || (isMarketOnly && !acknowledgeMarketOnly)}
        onClick={onContinue}
      >
        {blockers.length > 0 ? `Sign-off blocked (${blockers.length})` : 'Continue to sign-off'}
      </Button>
    </div>
  )
}
